import { FC, useEffect, useState } from "react";
import { Box, Avatar, Chip, Divider } from "@mui/material";
import { H6, Small, Span, Paragraph } from "../../components/Typography";
import ProgressIndicator from "../../components/UI/ProgressIndicator";
import FlexBox from "../../components/FlexBox";
import { formatDateToDDMMYYYY } from "../../utils/date_formatter";
import { WorkflowRequestService } from "../WorkflowRequest/services/WorkflowRequest.service";
import toast from "react-hot-toast";
import moment from "moment";
import { IWorkflowTaskDisplay } from "./models/WorkflowComment.interface";

interface IWorkflowCommentContentProps {
  requestId: string;
}

const WorkflowCommentContent: FC<IWorkflowCommentContentProps> = ({
  requestId,
}) => {
  const [tasks, setTasks] = useState<IWorkflowTaskDisplay[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchWorkflowTasks = async () => {
    try {
      setLoading(true);
      const requestDetails: any =
        await WorkflowRequestService.getRequestDetails(requestId);

      if (requestDetails?.tasks) {
        const transformedTasks: IWorkflowTaskDisplay[] =
          requestDetails.tasks.map((task: any) => ({
            id: task.id,
            comment: task.reason,
            timestamp: task.updatedat,
            user: {
              id: task.actor?.uid || task.actor?.id,
              name: `${task.actor?.first_name || ""} ${
                task.actor?.last_name || ""
              }`.trim(),
              avatar: task.actor?.profilephoto || "",
              type: task.actor?.type || "",
            },
            approvalStatus: getStatusText(task.status),
            workflowLevelName: task.workflow_level_name,
            originalTask: task,
          }));

        // sort tasks by updatedat timestamp (newest first)
        const sortedTasks = transformedTasks.sort(
          (a: IWorkflowTaskDisplay, b: IWorkflowTaskDisplay) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );

        setTasks(sortedTasks);
      } else {
        setTasks([]);
      }
    } catch (error: any) {
      toast.error("Failed to load workflow reasons");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (requestId) {
      fetchWorkflowTasks();
    }
  }, [requestId]);

  const getStatusColor = (status?: number | string) => {
    const statusNum =
      typeof status === "string"
        ? status === "approved"
          ? 2
          : status === "declined"
          ? 3
          : status === "pending"
          ? 1
          : 0
        : status;

    switch (statusNum) {
      case 2: // approved
        return "#4CAF50";
      case 3: // declined
        return "#f44336";
      case 1: // pending
        return "#ff9800";
      case 0: // created
        return "#666";
      default:
        return "#666";
    }
  };

  const getStatusBgColor = (status?: number | string) => {
    const statusNum =
      typeof status === "string"
        ? status === "approved"
          ? 2
          : status === "declined"
          ? 3
          : status === "pending"
          ? 1
          : 0
        : status;

    switch (statusNum) {
      case 2: // approved
        return "#e8f5e8";
      case 3: // declined
        return "#ffebee";
      case 1: // pending
        return "#fff3e0";
      case 0: // created
        return "#f5f5f5";
      default:
        return "#f5f5f5";
    }
  };

  const getStatusText = (status?: number) => {
    switch (status) {
      case 2:
        return "approved";
      case 3:
        return "declined";
      case 1:
        return "pending";
      case 0:
        return "created";
      default:
        return "unknown";
    }
  };

  if (loading) {
    return (
      <FlexBox
        alignItems={"center"}
        justifyContent={"center"}
        sx={{ height: "200px" }}
      >
        <ProgressIndicator size={20} />
        <Span sx={{ marginLeft: 2, color: "#666" }}>
          Loading workflow approval reasons...
        </Span>
      </FlexBox>
    );
  }

  if (tasks?.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "300px",
          flexDirection: "column",
          backgroundColor: "#fafafa",
          borderRadius: "8px",
          margin: "16px 0",
        }}
      >
        <Box sx={{ textAlign: "center", padding: "32px" }}>
          <H6 sx={{ color: "#94A4C4", marginBottom: 1 }}>
            No Workflow Reasons Available
          </H6>
          <Small sx={{ color: "#94A4C4" }}>
            Approval reasons will appear here when workflow tasks are processed.
          </Small>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: "16px 0" }}>
      {/* Workflow Reasons Header */}
      <Box sx={{ marginBottom: 3 }}>
        <FlexBox alignItems="center" justifyContent="space-between">
          <H6 sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}>
            Workflow Approval Reasons ({tasks.length})
          </H6>
          <Small sx={{ color: "#94A4C4" }}>Latest activity</Small>
        </FlexBox>
        <Divider sx={{ marginTop: 1 }} />
      </Box>

      {/* Tasks List */}
      <Box
        sx={{
          height: "calc(100vh - 300px)",
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: "8px",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: "#a8a8a8",
            },
          },
        }}
      >
        {tasks?.map((task: IWorkflowTaskDisplay, index: number) => {
          const isLastTask = index === tasks.length - 1;

          return (
            <Box key={task.id} sx={{ position: "relative" }}>
              {/* Timeline Line */}
              {!isLastTask && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "20px",
                    top: "60px",
                    bottom: "-20px",
                    width: "2px",
                    backgroundColor: "#e0e0e0",
                    zIndex: 0,
                  }}
                />
              )}

              {/* Task Card */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "24px",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {/* Avatar */}
                <Avatar
                  alt={task?.user?.name}
                  src={task?.user?.avatar}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: "3px solid #fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  {task?.user?.name?.charAt(0)?.toUpperCase()}
                </Avatar>

                {/* Task Content */}
                <Box sx={{ flex: 1, marginLeft: "16px" }}>
                  {/* User Info & Timestamp */}
                  <FlexBox alignItems="center" sx={{ marginBottom: "8px" }}>
                    <Span
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#333",
                      }}
                    >
                      {task?.user?.name}
                    </Span>
                    <Chip
                      label={`${task?.workflowLevelName}`}
                      size="small"
                      sx={{
                        marginLeft: 1,
                        height: "20px",
                        fontSize: "10px",
                        backgroundColor: "#e3f2fd",
                        color: "#1976d2",
                      }}
                    />
                    <Small
                      sx={{
                        fontSize: "11px",
                        color: "#94A4C4",
                        marginLeft: "auto",
                      }}
                    >
                      {moment(task?.timestamp).fromNow()}
                    </Small>
                  </FlexBox>

                  {/* Task Bubble */}
                  <Box
                    sx={{
                      backgroundColor: "#f8f9fa",
                      borderRadius: "12px",
                      padding: "12px 16px",
                      border: "1px solid #e9ecef",
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: "-8px",
                        top: "12px",
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderRight: "8px solid #e9ecef",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: "-7px",
                        top: "12px",
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderRight: "8px solid #f8f9fa",
                      },
                    }}
                  >
                    <Paragraph
                      sx={{
                        fontSize: "13px",
                        lineHeight: 1.5,
                        color: task?.comment ? "#333" : "#999",
                        margin: 0,
                        fontStyle: task?.comment ? "normal" : "italic",
                      }}
                    >
                      {task?.comment ||
                        "No reason provided for this workflow action"}
                    </Paragraph>

                    {/* Status Badge */}
                    <Box sx={{ marginTop: "12px" }}>
                      <Chip
                        label={task?.approvalStatus?.toUpperCase()}
                        size="small"
                        sx={{
                          fontSize: "10px",
                          fontWeight: 600,
                          height: "24px",
                          backgroundColor: getStatusBgColor(
                            task?.approvalStatus,
                          ),
                          color: getStatusColor(task?.approvalStatus),
                          border: `1px solid ${getStatusColor(
                            task?.approvalStatus,
                          )}20`,
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Workflow Level Info */}
                  <Small
                    sx={{
                      fontSize: "10px",
                      color: "#94A4C4",
                      marginTop: "4px",
                      marginLeft: "16px",
                      display: "block",
                    }}
                  >
                    {formatDateToDDMMYYYY(task?.timestamp)}
                  </Small>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
export default WorkflowCommentContent;
